import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { Transaction } from '../types/Transaction';
import FloatingActionButton from '../components/common/FloatingActionButton';
import TransactionModalWithDelete from '../components/calendar/TransactionModalWithDelete';
import SummaryCard from '../components/dashboard/SummaryCard';
import CalendarView from '../components/calendar/CalendarView';
import { useCalendar } from '../hooks/useCalendar';
import RecurringScopeDialog, { RecurringScope } from '../components/transactions/RecurringScopeDialog';
import { useAuth } from '../context/AuthContext';
import { useFormatting } from '../context/FormattingContext';
import { toUTCNoonDate } from '../utils/dateUtils';
import { formatDateInTimeZone, getMonthBoundsDateKeysInTimeZone } from '../utils/formatting';
import { authFetch } from '../lib/authFetch';

type CalendarSummaryStats = {
  startingCommitted: number;
  tentativeNet: number;
  projectedEnd: number;
};

type CalendarDropPayload = {
  txId: string;
  newDateISO: string;
  revert: () => void;
};

export const getDeleteTransactionUrl = (tx: Pick<Transaction, '_id' | 'source'>): string =>
  `/api/ledger/transactions/${tx._id}`;

const MyCalendarPage: React.FC = () => {
  const { auth } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [calendarRange, setCalendarRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const [prefillDate, setPrefillDate] = useState<string | undefined>(undefined);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeMode, setScopeMode] = useState<'edit' | 'delete' | 'reschedule'>('edit');
  const [scopeTx, setScopeTx] = useState<Transaction | null>(null);
  const [pendingDrop, setPendingDrop] = useState<CalendarDropPayload | null>(null);
  const [seriesScope, setSeriesScope] = useState<Exclude<RecurringScope, 'instance'> | null>(null);
  const formatting = useFormatting();

  const { data: calendarData } = useCalendar(calendarRange, true, refreshKey);

  const daily = useMemo(() => calendarData?.daily ?? [], [calendarData?.daily]);
  const monthBounds = useMemo(() => {
    const referenceDateKey = calendarRange?.startDate || calendarData?.range?.startDate;
    return getMonthBoundsDateKeysInTimeZone(formatting.timeZone, referenceDateKey);
  }, [calendarData?.range?.startDate, calendarRange?.startDate, formatting.timeZone]);

  const monthDaily = useMemo(
    () =>
      daily.filter((entry) => entry.date >= monthBounds.startDateKey && entry.date <= monthBounds.endDateKey),
    [daily, monthBounds.endDateKey, monthBounds.startDateKey]
  );

  const summaryStats = useMemo<CalendarSummaryStats>(() => {
    if (monthDaily.length === 0) {
      return {
        startingCommitted: 0,
        tentativeNet: 0,
        projectedEnd: 0,
      };
    }

    const first = monthDaily[0];
    const last = monthDaily[monthDaily.length - 1];
    return {
      startingCommitted: first.runningCommittedBalance - first.committedNetDelta,
      tentativeNet: monthDaily.reduce((sum, entry) => sum + entry.tentativeNetDelta, 0),
      projectedEnd: last.runningProjectedBalance,
    };
  }, [monthDaily]);

  const monthStartLabel = useMemo(
    () =>
      monthBounds.startDateKey
        ? formatDateInTimeZone(monthBounds.startDateKey, {
            locale: formatting.locale,
            timeZone: formatting.timeZone,
          })
        : '—',
    [formatting.locale, formatting.timeZone, monthBounds.startDateKey]
  );

  const monthEndLabel = useMemo(
    () =>
      monthBounds.endDateKey
        ? formatDateInTimeZone(monthBounds.endDateKey, {
            locale: formatting.locale,
            timeZone: formatting.timeZone,
          })
        : '—',
    [formatting.locale, formatting.timeZone, monthBounds.endDateKey]
  );

  useEffect(() => {
    document.title = 'My Calendar | Velora Money';
  }, []);

  const handleAdd = () => {
    setEditingTx(null);
    setPrefillDate(undefined);
    setModalOpen(true);
  };

  const isSeriesReady = (tx?: Transaction | null): boolean => Boolean(tx?.isRecurring && tx?.seriesId);

  const getAnchorDate = (tx: Transaction): string =>
    tx.date && tx.date.length >= 10 ? tx.date.slice(0, 10) : format(new Date(), 'yyyy-MM-dd');

  const closeScope = () => {
    setScopeOpen(false);
    setScopeTx(null);
    setPendingDrop(null);
  };

  const getResponseMessage = async (res: Response, fallback: string): Promise<string> => {
    try {
      const data = await res.json();
      return data?.message || fallback;
    } catch {
      return fallback;
    }
  };

  const handleSeriesDelete = async (tx: Transaction, scope: Exclude<RecurringScope, 'instance'>) => {
    if (!tx.seriesId) return;
    const confirmed =
      scope === 'future'
        ? window.confirm('Delete this and all future occurrences?')
        : window.confirm('Delete all occurrences in this series?');
    if (!confirmed) return;

    const params = new URLSearchParams({ scope });
    if (scope === 'future') params.set('anchorDate', getAnchorDate(tx));

    try {
      const res = await authFetch(`/api/ledger/series/${tx.seriesId}?${params.toString()}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (!res.ok) {
        throw new Error(await getResponseMessage(res, 'Failed to delete recurring series'));
      }
      closeScope();
      setModalOpen(false);
      setEditingTx(null);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete recurring series';
      alert(message);
    }
  };

  const handleInstanceDelete = async (tx: Transaction) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the "${tx.category}" transaction of $${tx.amount.toFixed(2)}?`
    );
    if (!confirmed) return;
    try {
      const res = await authFetch(`/api/ledger/transactions/${tx._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (!res.ok) {
        throw new Error(await getResponseMessage(res, 'Failed to delete transaction'));
      }
      closeScope();
      setModalOpen(false);
      setEditingTx(null);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete transaction';
      alert(message);
    }
  };

  const buildInstanceReschedulePayload = (tx: Transaction, newDateISO: string): Record<string, unknown> => {
    if (!tx.kind) {
      throw new Error('This transaction kind cannot be rescheduled by drag and drop yet.');
    }

    const payload: Record<string, unknown> = {
      amount: Number(tx.amount),
      kind: tx.kind,
      category: tx.category,
      description: tx.description || '',
      tentative: Boolean(tx.tentative),
      ownerScope: tx.ownerScope || 'household',
      visibility: tx.visibility || 'shared',
      date: toUTCNoonDate(newDateISO),
      tags: tx.tags || [],
    };

    if (tx.paymentStatus) payload.paymentStatus = tx.paymentStatus;
    if (tx.paidAt !== undefined) payload.paidAt = tx.paidAt;

    if (tx.kind === 'income' || tx.kind === 'expense' || tx.kind === 'cardPurchase' || tx.kind === 'adjustment') {
      if (!tx.accountId) throw new Error('Missing account for this transaction.');
      payload.accountId = tx.accountId;
      if (tx.kind === 'adjustment') {
        payload.amountSigned = typeof tx.amountSigned === 'number' ? tx.amountSigned : Number(tx.amount);
      }
    }

    if (tx.kind === 'transfer' || tx.kind === 'cardPayment') {
      if (!tx.fromAccountId || !tx.toAccountId) throw new Error('Missing transfer accounts for this transaction.');
      payload.fromAccountId = tx.fromAccountId;
      payload.toAccountId = tx.toAccountId;
    }

    if (tx.isRecurring) {
      payload.isRecurring = true;
      if (tx.frequency) payload.frequency = tx.frequency;
      if (tx.endDate) payload.endDate = tx.endDate;
      if (tx.nextDueDate) payload.nextDueDate = tx.nextDueDate;
    }

    return payload;
  };

  const applyInstanceReschedule = async (tx: Transaction, newDateISO: string, revert: () => void) => {
    try {
      const payload = buildInstanceReschedulePayload(tx, newDateISO);
      const res = await authFetch(`/api/ledger/transactions/${tx._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(await getResponseMessage(res, 'Failed to reschedule transaction'));
      }

      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      revert();
      const message = err instanceof Error ? err.message : 'Failed to reschedule transaction';
      alert(message);
    }
  };

  const handleScopeSelect = (scope: RecurringScope) => {
    if (!scopeTx) return;

    if (scopeMode === 'reschedule') {
      const drop = pendingDrop;
      const tx = scopeTx;
      closeScope();

      if (!drop) return;

      if (scope === 'instance') {
        void applyInstanceReschedule(tx, drop.newDateISO, drop.revert);
        return;
      }

      drop.revert();
      alert('Series reschedule not supported yet; reschedule a single instance.');
      return;
    }

    if (scopeMode === 'edit') {
      if (scope === 'instance') {
        closeScope();
        setEditingTx(scopeTx);
        setSeriesScope(null);
        setPrefillDate(undefined);
        setModalOpen(true);
        return;
      }
      closeScope();
      setSeriesScope(scope);
      setEditingTx(scopeTx);
      setPrefillDate(undefined);
      setModalOpen(true);
      return;
    }

    if (scope === 'instance') {
      void handleInstanceDelete(scopeTx);
      return;
    }
    void handleSeriesDelete(scopeTx, scope);
  };

  const seriesSubmitOverride =
    editingTx?.seriesId && seriesScope
      ? async (payload: Record<string, unknown>) => {
          const params = new URLSearchParams({ scope: seriesScope });
          if (seriesScope === 'future') params.set('anchorDate', getAnchorDate(editingTx));
          const excludedKeys = new Set(['date', 'paymentStatus', 'paidAt']);
          const payloadWithoutDate = Object.fromEntries(
            Object.entries(payload).filter(([key]) => !excludedKeys.has(key))
          );
          const res = await authFetch(`/api/ledger/series/${editingTx.seriesId}?${params.toString()}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.token}`,
            },
            body: JSON.stringify({ ...payloadWithoutDate, mutationReason: 'series_edit' }),
          });
          if (!res.ok) {
            const message = await getResponseMessage(res, 'Failed to update recurring series');
            alert(message);
            throw new Error(message);
          }
        }
      : undefined;

  const handleEdit = (tx: Transaction) => {
    if (isSeriesReady(tx)) {
      setScopeTx(tx);
      setScopeMode('edit');
      setScopeOpen(true);
      return;
    }
    setEditingTx(tx);
    setPrefillDate(undefined);
    setModalOpen(true);
  };

  const handleDeleteClick = (tx: Transaction): boolean => {
    if (!isSeriesReady(tx)) return false;
    setScopeTx(tx);
    setScopeMode('delete');
    setScopeOpen(true);
    return true;
  };

  const handleEventDrop = (drop: CalendarDropPayload) => {
    const events = calendarData?.events || [];
    const tx = events.find((event) => event._id === drop.txId);
    if (!tx) {
      drop.revert();
      alert('Unable to find the dropped transaction. Please refresh and try again.');
      return;
    }

    if (isSeriesReady(tx)) {
      setScopeTx(tx);
      setScopeMode('reschedule');
      setPendingDrop(drop);
      setScopeOpen(true);
      return;
    }

    void applyInstanceReschedule(tx, drop.newDateISO, drop.revert);
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack data-testid="mycalendar-page" spacing={{ xs: 2.5, md: 3 }} sx={{ minHeight: 0 }}>
        <Paper
          sx={{
            p: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(255, 255, 255, 0.98) 55%, rgba(46, 125, 50, 0.08) 100%)',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack spacing={1.25} sx={{ maxWidth: 760 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 999,
                bgcolor: 'rgba(25, 118, 210, 0.10)',
                color: 'primary.main',
                width: 'fit-content',
              }}
            >
              <CalendarMonthIcon fontSize="small" />
              <Typography variant="body2" fontWeight={700}>
                Planning center
              </Typography>
            </Box>
            <Typography
              variant="h4"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.25rem' },
                letterSpacing: '-0.02em',
              }}
            >
              <CalendarMonthIcon /> My Calendar
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              Visualize your financial activity — track past, current, and upcoming transactions in a calendar view.
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack spacing={{ xs: 2, sm: 2.5 }}>
            <Box>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Monthly overview
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                📆 Calendar Summary
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                View your income, expenses, transfers, and card payments by due date.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(200px, 1fr))',
                  lg: 'repeat(3, minmax(220px, 1fr))',
                },
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <SummaryCard
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Starting Cash Balance
                    <Tooltip title="Checking-account cash balance at the start of the month, based on committed transactions.">
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                }
                value={summaryStats.startingCommitted ?? 0}
                dateRange={monthBounds.startDateKey ? `As of ${monthStartLabel}` : undefined}
              />
              <SummaryCard
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Projected Cash Balance
                    <Tooltip title="Net change from tentative transactions affecting checking-account cash during the month.">
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                }
                value={summaryStats.tentativeNet ?? 0}
                dateRange={
                  monthBounds.startDateKey && monthBounds.endDateKey
                    ? `${monthStartLabel}-${monthEndLabel}`
                    : undefined
                }
              />
              <SummaryCard
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Projected End Balance
                    <Tooltip title="Projected checking-account cash balance at the end of the month, including committed and tentative transactions.">
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                }
                value={summaryStats.projectedEnd ?? 0}
                dateRange={monthBounds.endDateKey ? `As of ${monthEndLabel}` : undefined}
              />
            </Box>
          </Stack>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ minHeight: 0 }}>
            <Box>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Calendar view
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                📆 Calendar View
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 820 }}>
                View your income, expenses, transfers, and card payments by due date.
              </Typography>
            </Box>

            <Box
              data-testid="calendar-viewport"
              sx={{
                maxWidth: 1260,
                width: '100%',
                margin: '0 auto',
                overflowY: 'visible',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2.5,
                bgcolor: 'background.paper',
                p: { xs: 1.25, sm: 1.75 },
              }}
            >
              <CalendarView
                events={calendarData?.events || []}
                daily={calendarData?.daily || []}
                onRangeChange={(start, end) => {
                  setCalendarRange({ startDate: start, endDate: end });
                }}
                onEventClick={(tx) => handleEdit(tx)}
                onDateClick={(date) => {
                  setEditingTx(null);
                  setPrefillDate(date);
                  setModalOpen(true);
                }}
                onEventDrop={handleEventDrop}
              />
            </Box>
          </Stack>
        </Paper>

        <FloatingActionButton onClick={handleAdd} />

        <TransactionModalWithDelete
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingTx(null);
            setSeriesScope(null);
          }}
          onSuccess={() => {
            setModalOpen(false);
            setEditingTx(null);
            setPrefillDate(undefined);
            setSeriesScope(null);
            setRefreshKey((prev) => prev + 1);
          }}
          transaction={editingTx}
          defaultDate={prefillDate}
          onDeleteClick={handleDeleteClick}
          submitOverride={seriesSubmitOverride}
          disableDateEdit={Boolean(seriesScope)}
          disablePaymentEdit={Boolean(seriesScope)}
        />

        <RecurringScopeDialog
          open={scopeOpen}
          mode={scopeMode}
          transaction={scopeTx}
          onClose={closeScope}
          onSelect={handleScopeSelect}
        />
      </Stack>
    </Container>
  );
};

export default MyCalendarPage;
